import React, { FC, useEffect, useMemo, useState } from 'react';
import { Container } from './Callback.Styled';
import { ImageType } from '../../SellTrade/types/SellTradeForm.types';
import validator from 'validator';
import { contactUs } from '../../../api/contactUs';
import { useDispatch } from 'react-redux';
import { reducerTypes } from '../../../store/Alert/types';
import { CallbackEntityEnum, CallbackType, OrderServiceTypeEnum } from './types';
import { useAppSelector } from '../../../hooks/reduxHooks';
import 'react-phone-number-input/style.css';
import flags from 'react-phone-number-input/flags';
import { serviceOrders } from '../../../api/serviceOrder';
import { toggleLoadingAction } from '../../../store/Loading/action';
import { Typography, Box, Button, Tooltip } from '@mui/material';
import { Files, FileWrapper, PhoneField } from '../../ServicePage/ContactService/styles';
import { UploadFile } from '../../ServicePage/ContactService/UploadFile';
import closeIcon from '../../../assets/images/svg/closeIcon.svg';
import { InfoOutlined } from '@mui/icons-material';
import { RobotoFont } from '../../../styles/fonts';
import Image from 'next/image';
import { sendGTMEvent } from '@next/third-parties/google';

const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    fontSize: '16px',
    lineHeight: '20px',
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    backgroundColor: '#f0f4f8',
    color: '#C8C8C8',
    width: '100%',
    '&:hover': {
      backgroundColor: '#e6f5fe',
    },
  }),
  input: (provided: any) => ({
    ...provided,
    padding: '6.2px 0',
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: '400',
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    color: '#C8C8C8',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 400,
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    marginLeft: '12px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 400,
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    color: '#C8C8C8',
    marginLeft: '12px',
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    height: '42px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    outline: 'none',
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    boxShadow: 'none',
    color: '#C8C8C8',
  }),
  menu: (provided: any, state: any) => ({
    ...provided,
    zIndex: 5,
    backgroundColor: '#f0f4f8',
  }),
  dropdownIndicator: (provided: any) => ({
    paddingRight: 5,
    paddingTop: 4,
  }),
};

type Props = {
  manageModal?: () => void;
  contactType?: 'Callback' | 'Order' | 'Contact us';
  serviceType?: string;
  price?: string;
  contactForm?: boolean;
  serviceTypeEnum: OrderServiceTypeEnum | null;
};

const initialData = {
  entityId: null,
  entityType: CallbackEntityEnum.Product,
  email: '',
  phone: '',
  phoneCode: '',
  name: '',
  comment: '',
  images: [],
  serviceType: '',
  serviceTypeEnum: null,
  price: '',
};

const Callback: FC<Props> = ({
  manageModal,
  contactType = 'Callback',
  serviceType,
  price,
  contactForm,
  serviceTypeEnum,
}) => {
  const [phone, setPhone] = useState<any>(undefined);
  const { product } = useAppSelector((st) => st.products);
  const [data, setData] = useState<CallbackType>({
    ...initialData,
    serviceType: serviceType || '',
    price: price || '',
    serviceTypeEnum: serviceTypeEnum ?? null,
  });
  const dispatch = useDispatch();
  const locale = useAppSelector((st) => st.locale);

  const messages = useMemo(() => {
    return {
      title: locale[`${contactType.toLowerCase().replaceAll(' ', '_')}_title`],
      alert: locale['callback_apply_form_alert'],
      alert_failed: locale['callback_apply_form_alert_failed'],
      contact_us_block: locale['callback_contact_form_contact_us_block'],
      not_callback_name: locale['callback_contact_form_not_callback_name'],
      not_callback_placeholder_name: locale['callback_contact_form_not_callback_placeholder_name'],
      not_callback_email: locale['callback_contact_form_not_callback_email'],
      not_callback_placeholder_email: locale['callback_contact_form_not_callback_placeholder_email'],
      or: locale['callback_contact_form_not_callback_or'],
      not_callback_phone: locale['callback_contact_form_not_callback_phone'],
      not_callback_placeholder_phone: locale['callback_contact_form_not_callback_placeholder_phone'],
      contact_us_comments: locale['callback_contact_form_contact_us_comments'],
      comments_contact_form_true: locale['callback_contact_form_contact_us_comments_contact_form_true'],
      comments_contact_form_empty: locale['callback_contact_form_contact_us_comments_contact_form_empty'],
      not_contact_add_photos: locale['callback_not_callback_and_not_contact_add_photos'],
      not_contact_add_photos_info: locale['callback_not_callback_and_not_contact_add_photos_info'],
      button_text_submit: locale['submit'],
      button_text_cancel: locale['cancel'],
      selected_service_text: locale['callback_selected_service_text'],
      price_text: locale['callback_price_text'],
      serviceOptions: [
        { value: 'business', label: locale['callback_service_options_business'], price: '350' },
        { value: 'premium', label: locale['callback_service_options_premium'], price: '500' },
        { value: 'exclusive', label: locale['callback_service_options_exclusive'], price: '1000' },
      ],
    };
  }, [contactType, locale]);

  useEffect(() => {
    contactType === 'Callback' &&
      setData((prev) => ({
        ...prev,
        entityId: product?.id || null,
      }));
  }, [contactType, product?.id]);

  function validateInfo() {
    let errors = [];
    if (!validator.isEmail(data.email || '')) {
      errors.push('email');
    }
    if (!validator.isMobilePhone(phone || '')) {
      errors.push('phone');
    }
    return errors.length < 2;
  }

  async function applyForm() {
    if (!validateInfo()) {
      alert(`${messages.alert}`);
      return;
    }
    try {
      dispatch(toggleLoadingAction());
      if (contactType !== 'Order') {
        sendGTMEvent({ event: 'form_contact_us' });
        await contactUs({
          ...data,
          phone: phone || null,
          email: data.email ? data.email : null,
          entityType: contactType === 'Callback' ? CallbackEntityEnum.Product : CallbackEntityEnum.Service,
          images: data.images.map((image) => image.data),
        });
      } else {
        switch (data?.serviceTypeEnum) {
          case OrderServiceTypeEnum.Business:
            sendGTMEvent({ event: 'form_maintenance_business' });
            break;
          case OrderServiceTypeEnum.Premium:
            sendGTMEvent({ event: 'form_maintenance_premium' });
            break;
          case OrderServiceTypeEnum.Exclusive:
            sendGTMEvent({ event: 'form_maintenance_exclusive' });
            break;
        }

        await serviceOrders({
          ...data,
          phone: phone || null,
          email: data.email ? data.email : null,
          entityType: CallbackEntityEnum.Service,
          serviceName: data?.serviceType,
          stringImages: data.images.map((image) => image.data),
        });
      }
      dispatch({ type: reducerTypes.TOGGLE_ALERT });
      manageModal && manageModal();
    } catch (e) {
      console.log(e);
      alert(`${messages.alert_failed}`);
    } finally {
      dispatch(toggleLoadingAction());
    }
  }

  function removeImage(image: ImageType) {
    setData((prev) => ({
      ...prev,
      images: prev.images.filter((item) => item.id !== image.id),
    }));
  }

  return (
    <Container>
      {contactForm ? null : (
        <>
          <Typography
            variant='h3'
            style={{
              fontSize: '36px',
              lineHeight: '44px',
              fontWeight: 450,
              color: '#181818',
              textAlign: 'center',
              margin: '46px 0 6px',
            }}
          >
            {serviceType ? serviceType : messages.title}
            {price ? (
              <>
                <br />
                {locale['from'].toLowerCase()} {price} €
              </>
            ) : (
              ''
            )}
          </Typography>
          <Typography
            variant='body2'
            style={{
              fontSize: '14px',
              lineHeight: '24px',
              color: '#535353',
              fontWeight: 300,
              textAlign: 'center',
              marginBottom: `${contactType === 'Callback' ? '0px' : '32px'}`,
              maxWidth: '299px',
              letterSpacing: '-0.64px',
              transform: 'rotate(-0.28deg)',
            }}
          >
            {messages.contact_us_block}
          </Typography>
        </>
      )}
      {contactType === 'Callback' ? (
        <div className='link-container'>
          <span className='callback-link'>
            {' '}
            <a href='tel:+420 739 04 10 10'>+420 739 04 10 10</a>
          </span>
          <span className='callback-link'>
            <a href='mailto:info@chrono1010.watch'>info@chrono1010.watch</a>
          </span>
        </div>
      ) : null}
      <div
        className={`form-row ${
          contactType === 'Contact us' ? 'contact-us-row' : contactType === 'Order' ? 'form-row-order' : ''
        }`}
      >
        {contactType !== 'Callback' ? (
          <div className='input-wrapper'>
            <input
              type='text'
              value={data.name || ''}
              onChange={(e) => setData((prev) => ({ ...prev, name: e.target.value }))}
              className='custom-input'
              placeholder={`${messages.not_callback_placeholder_name}`}
            />
          </div>
        ) : null}
        <div className='input-wrapper'>
          <input
            type='email'
            value={data.email || ''}
            onChange={(e) => setData((prev) => ({ ...prev, email: e.target.value }))}
            className='custom-input'
            placeholder={`${messages.not_callback_placeholder_email}`}
          />
        </div>
        {contactType === 'Callback' ? (
          <Typography
            variant='h4'
            style={{
              fontSize: '18px',
              lineHeight: '22px',
              color: '#535353',
              margin: '1px 0',
            }}
          >
            {messages.or}
          </Typography>
        ) : null}
        <div className='input-wrapper  input-wrapper-right'>
          <div className='group-container' style={{ position: 'relative' }}>
            <Tooltip
              title={<p style={{ fontSize: '13px', fontWeight: 'normal' }}>{locale.service_page_phone_safe_note}</p>}
              placement='top-start'
              enterTouchDelay={0}
            >
              <InfoOutlined style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: '10px' }} />
            </Tooltip>
            <PhoneField
              placeholder={`${messages.not_callback_placeholder_phone}`}
              defaultCountry='CZ'
              value={phone}
              onChange={setPhone}
              flags={flags}
              className='custom-input'
              focusInputOnCountrySelection
              international
            />
          </div>
        </div>
        {/* {contactType === 'Order' ? (
          <>
            <div className='input-wrapper  input-wrapper-right'>
              <Select
                options={messages.serviceOptions}
                value={
                  messages.serviceOptions?.find(
                    (item) => item?.label?.toLowerCase() === data?.serviceType?.toLowerCase()
                  ) || messages.serviceOptions[0]
                }
                onChange={(option) =>
                  setData((prev) => ({
                    ...prev,
                    serviceType: option?.label || serviceType,
                    price: option?.price || price,
                  }))
                }
                className='custom-select'
                placeholder='Select service'
                dropdownStyles={customStyles}
                isClearable={false}
              />
            </div>
            <div className='input-wrapper'>
              <input
                type='text'
                value={`${data.price} €`}
                onChange={(e) => {}}
                className='custom-input disabled-input'
                readOnly
                disabled
              />
            </div>
          </>
        ) : null} */}
        {contactType === 'Contact us' ? (
          <div className='text-area-container contact-us-text-area'>
            <textarea
              className='custom-input text-area'
              value={data?.comment}
              onChange={(e) => setData((prev) => ({ ...prev, comment: e.target.value }))}
              placeholder={
                contactForm ? `${messages.comments_contact_form_true}` : `${messages.comments_contact_form_empty}`
              }
            />
            <Box style={{ width: '100%' }}>
              <Typography
                variant='body2'
                style={{
                  fontSize: '16px',
                  letterSpacing: '2px',
                  color: '#181818',
                  marginBottom: '4px',
                  marginTop: '24px',
                }}
              >
                {locale.general_attachment_star}
              </Typography>
              <UploadFile
                minHeight='88px'
                getFiles={(files: ImageType[]) => {
                  setData((prev) => ({
                    ...prev,
                    images: [...prev.images, ...files],
                  }));
                }}
                title={locale.general_upload_file}
              />
              <Files style={{ maxWidth: '300px', display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
                {data?.images?.map((image) => (
                  <FileWrapper>
                    <span>{`${image?.name.slice(0, 20)}${image?.name?.length > 20 ? '...' : ''}`}</span>
                    <div className='close-icon-image' onClick={() => removeImage(image)}>
                      <Image
                        src={closeIcon}
                        alt='Close icon'
                        style={{ position: 'relative', top: '-5px', width: '16px', height: '16px' }}
                      />
                    </div>
                  </FileWrapper>
                ))}
              </Files>
            </Box>
          </div>
        ) : null}
        {contactType !== 'Callback' && !contactForm ? (
          <>
            <Box style={{ width: '100%' }}>
              <Typography
                variant='body2'
                style={{ fontSize: '16px', letterSpacing: '1px', color: '#181818', marginBottom: '4px' }}
              >
                {messages.not_contact_add_photos}
              </Typography>
              <UploadFile
                minHeight='88px'
                getFiles={(files: ImageType[]) => {
                  setData((prev) => ({
                    ...prev,
                    images: [...prev.images, ...files],
                  }));
                }}
                title={locale.general_upload_file}
              />
              <Files style={{ maxWidth: '300px', display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
                {data?.images?.map((image) => (
                  <FileWrapper>
                    <span>{`${image?.name.slice(0, 20)}${image?.name?.length > 20 ? '...' : ''}`}</span>
                    <div className='close-icon-image' onClick={() => removeImage(image)}>
                      <Image
                        src={closeIcon}
                        alt='Close icon'
                        style={{ position: 'relative', top: '-5px', width: '16px', height: '16px' }}
                      />
                    </div>
                  </FileWrapper>
                ))}
              </Files>
            </Box>
          </>
        ) : null}
        {contactForm && contactType !== 'Contact us' ? <p></p> : ''}
        <div
          className={`${
            contactType === 'Callback' ? 'buttonsCallback' : contactType === 'Order' ? 'buttonsOrder' : 'buttons'
          }`}
        >
          {contactForm ? null : (
            <Button
              style={{
                width: '143px',
                height: '48px',
                marginTop: `${contactType === 'Callback' || contactType === 'Order' ? '0px' : '48px'}`,
              }}
              variant='outlined'
              onClick={() => {
                manageModal && manageModal();
              }}
            >
              <Typography variant='body2' style={{ fontSize: '16px', letterSpacing: '2px' }}>
                {messages.button_text_cancel}
              </Typography>
            </Button>
          )}
          {contactType !== 'Contact us' ? (
            <Button
              className='button-submit'
              style={{ width: '143px', height: '48px' }}
              onClick={applyForm}
              variant={'contained'}
            >
              <Typography variant='body2' style={{ fontSize: '16px', letterSpacing: '2px' }}>
                {messages.button_text_submit}
              </Typography>
            </Button>
          ) : (
            <Button
              className='contact-us-submit-button'
              style={{ width: '100%', height: '48px', marginTop: '9px' }}
              onClick={applyForm}
              variant={'outlined'}
            >
              <Typography variant='body2' style={{ fontSize: '16px', letterSpacing: '2px' }}>
                {messages.button_text_submit}
              </Typography>
            </Button>
          )}
        </div>
      </div>
    </Container>
  );
};

export default Callback;
